import {
  isAfter,
  parseISO,
  format,
  differenceInDays,
  differenceInCalendarDays,
  formatDistanceStrict
} from 'date-fns';
import { EMPTY_VALUE } from './string';

const HAS_TIMEZONE_OFFSET = /[zZ]|[+-]\d{2}:?\d{2}$/;

const policyCreationDateFormatter = new Intl.DateTimeFormat('en-GB', {
  dateStyle: 'short'
});

const policyCreationTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  timeStyle: 'short'
});

/**
 * Parse a datetime that is UTC when timezone information is absent.
 * Naive strings (e.g. "2023-07-26 14:00:00") are treated as UTC so the
 * browser can convert them to local time when formatting.
 */
export function parseUtcDateTime(value: string): Date {
  const trimmed = value.trim();
  if (HAS_TIMEZONE_OFFSET.test(trimmed)) {
    return new Date(trimmed);
  }

  const normalised = trimmed.includes('T')
    ? trimmed
    : trimmed.replace(' ', 'T');

  return new Date(`${normalised}Z`);
}

/** Epoch milliseconds for a UTC-when-naive datetime; `NaN` when blank or unparseable. */
export function utcTimestamp(value?: string | null): number {
  return value ? parseUtcDateTime(value).getTime() : NaN;
}

export const DATE_TIME_FORMAT = 'dd MMM yyyy, HH:mm';

/** "14 Jul 2026, 09:14" in local time; naive API datetimes are read as UTC. */
export function formatDateTime(value?: string | null): string {
  const ms = utcTimestamp(value);
  return Number.isNaN(ms) ? EMPTY_VALUE : format(ms, DATE_TIME_FORMAT);
}

/** "09:14:05" in local time; naive API datetimes are read as UTC. */
export function formatTime(value?: string | number | null): string {
  const ms = typeof value === 'number' ? value : utcTimestamp(value);
  return Number.isNaN(ms) ? EMPTY_VALUE : format(ms, 'HH:mm:ss');
}

/** "2h 14m", "35m" or "42s". */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return EMPTY_VALUE;
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m) return `${m}m`;
  return `${s}s`;
}

/**
 * Format a policy creation datetime for Policy Management (en-GB short date + time)
 * in the browser's local timezone.
 */
export function formatPolicyCreationDateTime(value: string): string {
  const date = parseUtcDateTime(value);
  return `${policyCreationDateFormatter.format(
    date
  )} ${policyCreationTimeFormatter.format(date)}`;
}

export function formatDateStr(
  dateString: string,
  formatStr = 'dd/MMM/yyyy - h:mm a'
) {
  return format(new Date(dateString), formatStr);
}
export function formatDayStr(dateString: string) {
  return formatDateStr(dateString, 'dd/MMM/yyyy');
}

/** Like `formatDateStr`, but blank or unparseable input gives `EMPTY_VALUE` instead of throwing. */
export function formatOptionalDate(
  value?: string | null,
  pattern = 'dd MMM yyyy'
): string {
  if (!value || !isValidDate(value)) return EMPTY_VALUE;
  return formatDateStr(value, pattern);
}

export function time(date = Date.now()) {
  return Math.floor(date / 1000);
}

export function isValidDate(dateString: string) {
  return !isNaN(Date.parse(dateString));
}

export const formatExpectedCloseDate = (
  date: string | undefined | null,
  formatStr = 'dd/MM/yyyy'
) => {
  return date && isAfter(parseISO(date), new Date(2000, 0, 1))
    ? formatDateStr(date, formatStr) || null
    : '-';
};

export const is18OrOlder = (dob: string): boolean => {
  if (!dob) return false;

  const birthDate = new Date(dob);
  const today = new Date();
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    return age - 1 >= 18;
  }

  return age >= 18;
};

/**
 * Check if a task is inactive (no activity for 30-120 days)
 * @param updatedAt - The updated_at date string from the task
 * @returns boolean indicating if the task is inactive
 */
export const isInactive = (updatedAt: string): boolean => {
  if (!updatedAt) return false;

  const lastUpdated = parseISO(updatedAt);
  const today = new Date();
  const daysSinceUpdate = differenceInDays(today, lastUpdated);

  return daysSinceUpdate >= 30 && daysSinceUpdate < 120;
};

/**
 * Check if a task is dormant (no activity for 120+ days)
 * @param updatedAt - The updated_at date string from the task
 * @returns boolean indicating if the task is dormant
 */
export const isDormant = (updatedAt: string): boolean => {
  if (!updatedAt) return false;

  const lastUpdated = parseISO(updatedAt);
  const today = new Date();
  const daysSinceUpdate = differenceInDays(today, lastUpdated);

  return daysSinceUpdate >= 120;
};

/**
 * "today", "1 day ago", "3 hours ago" for a published date. With `day` precision the
 * source gave a date only, so the answer is in whole days, never hours.
 */
export function formatPublishedAgo(
  value: string | null | undefined,
  precision: 'time' | 'day' | 'unknown' = 'time',
  now: Date = new Date()
): string {
  if (!value || precision === 'unknown') return '';
  const date = parseISO(value);
  if (Number.isNaN(date.getTime())) return '';
  if (precision === 'day') {
    const days = Math.max(0, differenceInCalendarDays(now, date));
    if (days === 0) return 'today';
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  }
  return formatDistanceStrict(date, now, { addSuffix: true });
}
